import { useState, type FormEvent } from "react";
import {
  Box, Button, Paper, Typography, Stack, Alert, CircularProgress, TextField, Divider, Link as MuiLink
} from "@mui/material";
import MicrosoftIcon from "@mui/icons-material/Window";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, BrowserAuthError } from "@azure/msal-browser";
import { useMutation } from "@tanstack/react-query";
import { loginRequest, clearStuckInteractionState } from "@/authConfig";
import { login, signup } from "@/api/auth";
import { setLocalAuthSession } from "@/localAuth";
import { getErrorMessage } from "@/utils/getErrorMessage";
import athrivIcon from "@/assets/athriv-icon.png";

type EmailMode = "login" | "signup";

export default function LoginPage() {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState<string | null>(null);
  // A pricing-page "Start Free Trial" button links here with ?signup=1 so the
  // signup form opens directly instead of the SSO-first screen.
  const [emailMode, setEmailMode] = useState<EmailMode | null>(
    () => (new URLSearchParams(window.location.search).get("signup") ? "signup" : null)
  );
  const [companyName, setCompanyName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // inProgress comes from MsalProvider's context (backed by MSAL's own event
  // stream), not a locally-tracked boolean - this is the correct way to know
  // whether an interactive call is already running, per MSAL's own guidance.
  const busy = inProgress !== InteractionStatus.None;

  const emailAuthMutation = useMutation({
    mutationFn: () =>
      emailMode === "signup"
        ? signup({ companyName, displayName, email, password })
        : login({ email, password }),
    onSuccess: (data) => {
      setLocalAuthSession(data);
      // Full navigation, not client-side - LoginPage renders before any Router
      // exists (see App.tsx), and the authenticated router has no "/login"
      // route, so without this the URL would stay on /login post-auth and
      // nothing would match once RouterProvider takes over.
      window.location.href = "/";
    },
    onError: (err: unknown) => setError(getErrorMessage(err))
  });

  function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    emailAuthMutation.mutate();
  }

  async function handleLogin() {
    setError(null);
    try {
      await instance.loginRedirect(loginRequest);
      // loginRedirect navigates the browser away on success, so normally
      // nothing after this line runs. Code below only matters when it
      // throws synchronously instead (bad config, stuck flag, popup-blocked
      // scenarios, etc).
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === "interaction_in_progress") {
        // Self-heal a stuck flag (see authConfig.ts) and retry exactly once,
        // rather than leaving the user stuck on a button that does nothing.
        clearStuckInteractionState();
        try {
          await instance.loginRedirect(loginRequest);
          return;
        } catch (retryErr) {
          console.error(retryErr);
        }
      } else {
        console.error(err);
      }
      setError("Sign-in couldn't start. Please refresh the page and try again.");
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2
      }}
    >
      <Paper elevation={0} sx={{ p: 5, width: 380, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 48, height: 48, objectFit: "contain" }} />
          <Box>
            <Typography variant="h5" sx={{ fontFamily: '"Manrope", sans-serif' }}>
              Athriv Technologies
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {emailMode === null
                ? "Sign in to continue."
                : emailMode === "signup"
                  ? "Create your company's account."
                  : "Log in with your email and password."}
            </Typography>
          </Box>

          {emailMode === null ? (
            <>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <MicrosoftIcon />}
                disabled={busy}
                onClick={handleLogin}
              >
                {busy ? "Signing in…" : "Sign in with Microsoft"}
              </Button>
              <Divider flexItem>or</Divider>
              <Button fullWidth variant="outlined" onClick={() => { setError(null); setEmailMode("login"); }}>
                Log in with email
              </Button>
              <Typography variant="body2" color="text.secondary">
                No Microsoft account?{" "}
                <MuiLink component="button" type="button" onClick={() => { setError(null); setEmailMode("signup"); }}>
                  Sign up your company
                </MuiLink>
              </Typography>
            </>
          ) : (
            <Stack component="form" spacing={2} sx={{ width: "100%" }} onSubmit={handleEmailSubmit}>
              {emailMode === "signup" && (
                <>
                  <TextField
                    label="Company name" size="small" fullWidth required
                    value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  />
                  <TextField
                    label="Your name" size="small" fullWidth required
                    value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  />
                </>
              )}
              <TextField
                label="Email" type="email" size="small" fullWidth required
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Password" type="password" size="small" fullWidth required
                value={password} onChange={(e) => setPassword(e.target.value)}
                helperText={emailMode === "signup" ? "At least 8 characters." : undefined}
              />
              <Button fullWidth variant="contained" type="submit" disabled={emailAuthMutation.isPending}>
                {emailAuthMutation.isPending
                  ? <CircularProgress size={18} color="inherit" />
                  : emailMode === "signup" ? "Sign up" : "Log in"}
              </Button>
              {emailMode === "login" && (
                <MuiLink href="/forgot-password" variant="body2">Forgot password?</MuiLink>
              )}
              <Stack direction="row" justifyContent="space-between">
                <MuiLink
                  component="button" type="button" variant="body2"
                  onClick={() => { setError(null); setEmailMode(emailMode === "signup" ? "login" : "signup"); }}
                >
                  {emailMode === "signup" ? "Already have an account?" : "New company? Sign up"}
                </MuiLink>
                <MuiLink component="button" type="button" variant="body2" onClick={() => { setError(null); setEmailMode(null); }}>
                  Back
                </MuiLink>
              </Stack>
            </Stack>
          )}

          {error && (
            <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
              {error}
            </Alert>
          )}

          {emailMode === null && (
            <Typography variant="caption" color="text.secondary">
              Companies using Microsoft 365 / Entra ID can sign in above with no separate
              password. Everyone else can create an account with email and password.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
